/* Error tracking in the browser — the other half of the API's.
 *
 * The redaction is `@eventuley/observability`, shared with the API because both
 * report to the same processor. What is here is only what differs: the browser
 * SDK, the options that decide what it collects, and how a DSN reaches a static
 * bundle at all.
 *
 * ── why the DSN is not a Vite variable ───────────────────────────────────────
 * `import.meta.env.VITE_SENTRY_DSN` would be baked in at BUILD time, which
 * means one image per environment — and `infra-coolify.md` promotes the same
 * image local → staging → production. So it arrives the same way the API's
 * upstream does: substituted into a tiny script by nginx at container start,
 * from `infra/web/nginx.conf.template`.
 *
 * That script is a plain `<script src>` rather than a fetch, so it is in place
 * before the bundle runs and an error during mount is still reported. In `npm
 * run dev` there is no nginx, the request 404s, nothing is defined, and error
 * tracking stays off — which is correct locally.
 */

import { scrubEvent } from '@eventuley/observability';
import * as Sentry from '@sentry/react';

/** Written by `/runtime-config.js`, which nginx renders per environment. */
interface RuntimeConfig {
  readonly sentryDsn?: string;
  /* The commit the image was built from, so an issue names a deploy. */
  readonly release?: string;
}

declare global {
  interface Window {
    __EVENTULEY__?: RuntimeConfig | undefined;
  }
}

export interface BrowserSentryOptions {
  readonly dsn?: string | undefined;
  readonly environment: string;
  readonly release: string;
}

/** What nginx substituted, or nothing at all. */
export function runtimeConfig(): RuntimeConfig {
  /* Guarded rather than read directly: the script legitimately does not exist
   * in development, and a page that fails to mount because a diagnostic tool is
   * missing would be a far worse defect than having no diagnostics. */
  try {
    return globalThis.window?.__EVENTULEY__ ?? {};
  } catch {
    return {};
  }
}

/**
 * Starts error tracking, or does nothing at all.
 *
 * Same contract as the API's: no DSN means no initialisation, and an empty
 * string counts as no DSN because that is what an unset variable substitutes
 * to. Returns whether it started.
 */
export function startErrorTracking(options: BrowserSentryOptions): boolean {
  if (!options.dsn) return false;

  Sentry.init({
    dsn: options.dsn,
    environment: options.environment,
    release: options.release,

    /* Off, as on the API. In a browser this additionally means no IP address
     * and no request headers — `security.md`'s redaction list names both. */
    sendDefaultPii: false,

    /* No tracing, no profiling. Same reasoning as the API: premature
     * instrumentation for a system with no users, and every span carries a URL.
     */
    tracesSampleRate: 0,

    /* **Session Replay is deliberately absent**, and must stay absent. It
     * records the DOM, which on this product means guest lists, contact details
     * and money. Turning it on would make Sentry a processor of customer
     * content rather than of diagnostics — a different entry in `data-map.md`
     * and a different conversation with customers. */

    beforeSend: (event) => scrubEvent(event),

    /* The browser SDK records clicks, navigations, console output and every
     * fetch by default. `scrubEvent` cleans what survives; keeping the number
     * low keeps that surface small. */
    maxBreadcrumbs: 20,

    /* Extensions and injected scripts throw inside pages they did not write,
     * and those errors are not ours. Without this the first real issue is
     * somebody's password manager. */
    ignoreErrors: ['ResizeObserver loop limit exceeded', 'ResizeObserver loop completed'],
  });

  return true;
}
